import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import { mergeIMDFFeatures } from "@/features/editor/utils/baseFeatureUtils";
import type { Building } from "@/libs/models";
import { pathsToPathDict } from "@/libs/models/path";
import type {
  GeofenceOverlayFeature,
  PoiOverlayFeature,
} from "../editor/types";

const sliceData = (state: RootState) => state.cartography;

export const selectBuildings = createSelector(sliceData, (item) => {
  return (
    Object.values(item.buildings)
      .map<Building>((b) => b.value)
      .sort((a, b) => a.name.localeCompare(b.name)) || []
  );
});

export const selectBuildingsLength = createSelector(sliceData, (item) => {
  return Object.values(item.buildings).length;
});

export const selectStoreBuildings = createSelector(sliceData, (item) => {
  return item.buildings;
});

export const selectCurrentBuildingID = createSelector(sliceData, (item) => {
  return item.currentBuildingID;
});

export const selectCurrentFloorID = createSelector(sliceData, (item) => {
  return item.currentFloorID;
});

export const selectCurrentPoiID = createSelector(sliceData, (item) => {
  return item.currentPoiID;
});

export const selectCurrentFeatureID = createSelector(sliceData, (item) => {
  return item.currentFeatureID;
});

export const selectCurrentBuilding = createSelector(sliceData, (data) => {
  if (
    Object.keys(data.buildings).length === 0 ||
    data.currentBuildingID == null
  ) {
    return null;
  }

  return data.buildings[data.currentBuildingID]?.value || null;
});

export const selectCurrentBuildingPois = createSelector(
  [sliceData, selectCurrentBuilding],
  (data, currentBuilding) => {
    if (!currentBuilding) return null;

    const { pois } = currentBuilding;
    if (!pois) return null;

    return data.visiblePoisFilter.length === 0
      ? pois
      : pois.getPoisByIds(data.visiblePoisFilter);
  },
);

export const selectVisiblePoisFilter = createSelector(
  sliceData,
  (data) => data.visiblePoisFilter,
);

export const selectCurrentFloor = createSelector(
  [sliceData, selectCurrentBuilding],
  (data, building) =>
    building?.floors?.find((f) => f.id === data.currentFloorID),
);

export const selectBuildingFloors = createSelector(
  [selectCurrentBuilding],
  (building) => building?.floors,
);

export const selectCurrentFloorLevel = createSelector(
  [selectCurrentFloor],
  (item) => {
    return item?.level;
  },
);

export const selectCurrentPoiCategoriesIDs = createSelector(
  sliceData,
  (item) => {
    return item.currentPoiCategoriesIDs;
  },
);

/**
 * Returns the current poi category id.
 *   - When a parent category is selected, the first id in the Redux selection array is the parent category id.
 *   - When a subcategory is selected, the first id in the Redux selection array is the subcategory id.
 */
export const selectCurrentPoiCategoryId = createSelector(
  selectCurrentPoiCategoriesIDs,
  (ids) => ids?.[0] ?? null,
);

export const selectCurrentFilterText = createSelector(sliceData, (item) => {
  return item.currentFilterText;
});

export const selectSearchHistory = createSelector(sliceData, (item) => {
  return item.searchHistory;
});

export const selectFavoritePois = createSelector(sliceData, (item) => {
  return item.favoritePois;
});

export const selectCurrentBuildingState = createSelector(sliceData, (data) => {
  if (
    Object.keys(data.buildings).length === 0 ||
    data.currentBuildingID == null
  ) {
    return null;
  }

  return data.buildings[data.currentBuildingID]?.state;
});

export const selectCurrentBuildingPathDict = createSelector(
  [selectCurrentBuilding],
  // Force pathsDict to use flatEarth conversion
  (building) => pathsToPathDict(building),
);

export const selectCurrentBuildingOverlayFeatures = createSelector(
  [selectCurrentBuilding],
  (building) => {
    const overlays = building?.overlays;
    const fences = building?.geofences?.geofences
      .map((f) => f.toGeojson())
      .reduce(
        (acc, f) => {
          acc[f.id] = f;
          return acc;
        },
        {} as Record<string, GeofenceOverlayFeature>,
      );

    const pois = building?.pois?.toGeojson().features.reduce(
      (acc, f: PoiOverlayFeature) => {
        acc[f.id] = f;
        return acc;
      },
      {} as Record<string, PoiOverlayFeature>,
    );

    const features = {
      ...(overlays ?? {}),
      ...(fences ?? {}),
      ...(pois ?? {}),
    };

    return features;
  },
);

/**
 * Selects the GeoJSON representation of the current building.
 *
 * Extracts the `geojson` object from the selected building, which contains
 * all IMDF features and metadata for the building.
 *
 * @returns The GeoJSON object of the current building.
 */
export const selectCurrentBuildingGeoJSON = createSelector(
  [selectCurrentBuilding],
  (building) => building?.geojson,
);

/**
 * Selects all IMDF features from the current building, mapped to their corresponding floors.
 *
 * This function retrieves all features from the building's GeoJSON and associates
 * each feature with its respective floor using the `floor_id` property.
 *
 * @returns An array of IMDF features with assigned floor IDs.
 */
export const selectIMDFFeatures = createSelector(
  [selectCurrentBuildingGeoJSON, selectBuildingFloors],
  (geojson, floors) => {
    if (!geojson) return [];

    return Object.entries(geojson.featuresByLevel).reduce(
      (acc, [levelOrdinal, features]) => {
        const floorOnLevel = floors.find(
          (floor) => floor.level === Number(levelOrdinal),
        );
        if (!floorOnLevel) return acc;
        const newFeatures = features.map((feature) => {
          feature.addProperty("floor_id", floorOnLevel.id);
          return feature;
        });
        acc.push(...newFeatures);
        return acc;
      },
      [],
    );
  },
);

/**
 * Selects the IMDF features for the currently active floor, applying editor changes if needed.
 *
 * This function retrieves all features for the selected floor level. If the user is in
 * base layer editing mode, it merges the current editor changes with the existing features.
 * Otherwise, it simply returns the floor features without modifications.
 *
 * @param state - The Redux store state.
 * @returns An array of IMDF features for the selected floor, with editor changes applied if applicable.
 */
export const selectIMDFFloorFeatures = createSelector(
  [
    (state: RootState) => state.baseLayersEditor.geojson.changes,
    selectCurrentBuildingGeoJSON,
    selectCurrentFloorLevel,
  ],
  (changes, geojson, floorLevel) => {
    if (!geojson || floorLevel === undefined || floorLevel == null) return [];
    const floorFeatures = geojson.getFeaturesByFloorLevel(floorLevel);
    const mergedFeatures = mergeIMDFFeatures(floorFeatures, changes);
    return Object.values(mergedFeatures);
  },
);
